import { Component, OnInit, OnDestroy, inject, ChangeDetectionStrategy, DestroyRef } from '@angular/core';

import { Router, NavigationEnd, ActivatedRoute, RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter, merge } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CanComponentDeactivate } from '../../core/guards/unsaved-changes.guard';
import { StelaLayoutContextService } from '../../shared/layout/services/stela-layout-context.service';
import { StelaBreadcrumbItem } from '../../shared/layout/models/stela-layout.models';

export interface BreadcrumbItem {
    title: string;
    url: string | null;
    translateKey?: string;
}

@Component({
    selector: 'app-breadcrumb',
    imports: [RouterOutlet],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `<router-outlet></router-outlet>`
})
export class BreadcrumbComponent implements OnInit, OnDestroy, CanComponentDeactivate {
    title: string = '';
    breadcrumb: BreadcrumbItem[] = [];
    private readonly destroyRef = inject(DestroyRef);
    private readonly layoutContext = inject(StelaLayoutContextService);
    private readonly translateService = inject(TranslateService);
    /** What this component last put in the layout context, to tell it apart from what a child set. */
    private ownPageTitle: string | null = null;
    private ownBreadcrumbs: StelaBreadcrumbItem[] | null = null;

    constructor(
        public router: Router,
        public activatedRoute: ActivatedRoute
    ) {}

    ngOnInit(): void {
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(() => {
            this.updateBreadcrumb();
        });
        // instant() returns the raw key until the translations arrive from the API, so on a direct load
        // of a deep URL the first pass shows 'dashboard.dashboard' and the page title key. Redo it once
        // they are in, but only for what this component still owns: a form may have replaced the title
        // or the breadcrumbs with its entity's (PersonFormComponent.updatePageContext), and the generic
        // route ones must not overwrite them.
        merge(
            this.translateService.onLangChange,
            this.translateService.onDefaultLangChange,
            this.translateService.onTranslationChange
        ).pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(() => {
            this.retranslateOwnContext();
        });
        this.updateBreadcrumb();
    }

    ngOnDestroy(): void {
        this.layoutContext.clearPageTitle();
        this.layoutContext.clearBreadcrumbs();
    }

    public updateBreadcrumb(): void {
        let route = this.activatedRoute;
        while (route.firstChild) {
            route = route.firstChild;
        }
        const data = route.snapshot.data;
        this.title = data['title'] || '';
        this.breadcrumb = data['breadcrumb'] || [];

        // Bridge: update StelaLayoutContextService so StelaPageHeadComponent renders the page head.
        // TranslateService.instant() throws on an empty key, and a route with no `data.title`
        // (e.g. /forbidden, reached by authGuard's redirect on a denied permission) would crash
        // this component — and with it every router-outlet it hosts — instead of just rendering
        // an untitled page.
        if (this.title) {
            this.setOwnPageTitle();
        } else {
            this.ownPageTitle = null;
            this.layoutContext.clearPageTitle();
        }

        this.setOwnBreadcrumbs();
    }

    private retranslateOwnContext(): void {
        if (this.ownPageTitle !== null && this.layoutContext.pageTitle() === this.ownPageTitle) {
            this.setOwnPageTitle();
        }
        if (this.ownBreadcrumbs !== null && this.layoutContext.breadcrumbs() === this.ownBreadcrumbs) {
            this.setOwnBreadcrumbs();
        }
    }

    private setOwnPageTitle(): void {
        this.ownPageTitle = this.translateService.instant(this.title);
        this.layoutContext.setPageTitle(this.ownPageTitle!);
    }

    private setOwnBreadcrumbs(): void {
        const dashboardItem = {
            label: this.translateService.instant('dashboard.dashboard'),
            route: '/dashboard/list'
        };
        const mappedItems = this.breadcrumb.map(item => ({
            label: this.translateService.instant(item.translateKey ?? item.title),
            route: item.url ?? undefined
        }));
        this.ownBreadcrumbs = [dashboardItem, ...mappedItems];
        this.layoutContext.setBreadcrumbs(this.ownBreadcrumbs);
    }

    // Implementation of CanComponentDeactivate for unsaved changes guard
    canDeactivate(): boolean {
        return true;
    }

    hasUnsavedChanges(): boolean {
        return false;
    }
}
